import { useState } from "react";
import { ReportFilters } from "../components/ReportFilters";
import { 
  useGetInvoiceReportQuery, 
  useLazyGetInvoiceReportCsvQuery,
  useLazyGetInvoiceReportPdfQuery 
} from "../services/reportsV2Api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { useSelector } from "react-redux";
import type { RootState } from "@/store";
import { format } from "date-fns";
import { toast } from "sonner";

export default function ClientInvoiceReport() {
  const [filters, setFilters] = useState({});
  const { user } = useSelector((state: RootState) => state.auth);
  const isSuperAdmin = user?.role === "SUPER_ADMIN" || user?.role === "ADMIN";
  
  const { data: reports, isLoading } = useGetInvoiceReportQuery(filters);
  const [triggerExportCsv, { isLoading: isExportingCsv }] = useLazyGetInvoiceReportCsvQuery();
  const [triggerExportPdf, { isLoading: isExportingPdf }] = useLazyGetInvoiceReportPdfQuery();

  const handleExportCsv = async (exportFilters: any) => {
    try {
      const blob = await triggerExportCsv(exportFilters).unwrap();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Invoice_Report_${format(new Date(), "yyyyMMdd")}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("CSV export started");
    } catch (err) {
      toast.error("Failed to export CSV");
    }
  };

  const handleExportPdf = async (exportFilters: any) => {
    try {
      const blob = await triggerExportPdf(exportFilters).unwrap();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Invoice_Report_${format(new Date(), "yyyyMMdd")}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("PDF export started");
    } catch (err) {
      toast.error("Failed to export PDF");
    }
  };

  const formatCurrency = (amount: number | undefined) => {
    if (amount === undefined || amount === null) return "-";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const isExporting = isExportingCsv || isExportingPdf;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {isSuperAdmin ? "Vendor Payable & Revenue Report" : "Client Invoice Report"}
          </h1>
          <p className="text-muted-foreground">
            {isSuperAdmin 
              ? "Track vendor costs, client billing and margins across all orders." 
              : "Review your invoices and service details."}
          </p>
        </div>
      </div>

      <ReportFilters 
        onSearch={setFilters} 
        onExportCsv={handleExportCsv}
        onExportPdf={handleExportPdf}
      />

      <Card>
        <CardContent className="p-0">
          {(isLoading || isExporting) ? (
            <div className="flex flex-col justify-center items-center py-20 gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              {isExporting && <p className="text-sm text-muted-foreground">Preparing your download...</p>}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Candidate</TableHead>
                    <TableHead>Services</TableHead>
                    <TableHead className="text-right">Amount Charged</TableHead>
                    {isSuperAdmin && <TableHead className="text-right">Vendor Cost</TableHead>}
                    {isSuperAdmin && <TableHead className="text-right text-emerald-600">Margin</TableHead>}
                    <TableHead>Clinic</TableHead>
                    <TableHead>Service Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reports?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isSuperAdmin ? 9 : 7} className="text-center py-10 text-muted-foreground">
                        No report data found for the selected filters.
                      </TableCell>
                    </TableRow>
                  ) : (
                    reports?.map((report: any, idx: number) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium underline cursor-pointer text-primary">
                          {report.invoiceNumber}
                        </TableCell>
                        <TableCell>{report.invoiceDate ? format(new Date(report.invoiceDate), "MMM dd, yyyy") : "-"}</TableCell>
                        <TableCell>{report.candidateName}</TableCell>
                        <TableCell className="max-w-[200px] truncate" title={report.services}>
                          {report.services}
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          {formatCurrency(report.totalAmount)}
                        </TableCell>
                        {isSuperAdmin && (
                          <TableCell className="text-right text-muted-foreground">
                            {formatCurrency(report.vendorCost)}
                          </TableCell>
                        )}
                        {isSuperAdmin && (
                          <TableCell className="text-right font-medium text-emerald-600">
                            {formatCurrency(report.margin)}
                          </TableCell>
                        )}
                        <TableCell>{report.clinicName}</TableCell>
                        <TableCell>{report.serviceDate ? format(new Date(report.serviceDate), "MMM dd, yyyy") : "-"}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
